<template>
	<div v-if="field">
		<div :ref="'footer_ref'" class="buttons-section card-footer-container flex-wrap" :class="[buttonsClass, { plain: plainMode }]">
			<AppDropdownMenu
				left
				bottom
				absolute
				min-width="200"
				content-class="men"
				:items="restOfButtonsOptions"
				v-if="restOfButtonsOptions.length > 0"
			>
				<template #activator>
					<AnemicButton @click="(e) => e.preventDefault()" :width="menuActivatorSize" :height="menuActivatorSize" class="three-dot-button">
						<SIcon name="three-dot-menu" width="13" class="menu-icon" />
					</AnemicButton>
				</template>
				<template #list-item="{ item }">
					<span v-if="!item.divider">
						{{ item.text }}
					</span>
				</template>
			</AppDropdownMenu>
			<div v-for="btn in buttonsOptions" :key="btn.id" class="">
				<FooterPrimaryButton v-if="isPrimary(btn)" @click="onButtonClick(btn)" :class="{ striped: !isRun }" :plainMode="plainMode">
					{{ btn.label }}
				</FooterPrimaryButton>
				<FooterSecondaryButton v-else @click="onButtonClick(btn)" :class="{ striped: !isRun }" :plainMode="plainMode">
					{{ btn.label }}
				</FooterSecondaryButton>
			</div>
			<FooterSecondaryButton
				v-if="fieldOptions.secondary_button_enabled"
				:class="{ striped: !isRun }"
				@click="$emit('onCancel')"
				:plainMode="plainMode"
			>
				{{ fieldOptions.secondary_button_value }}
			</FooterSecondaryButton>
			<FooterPrimaryButton
				v-if="fieldOptions.primary_button_enabled"
				:class="{ striped: !isRun }"
				:disabled="isSubmitButtonDisabled"
				@click="$emit('onDone')"
				:plainMode="plainMode"
			>
				{{ fieldOptions.primary_button_value }}
			</FooterPrimaryButton>
		</div>
	</div>
	<div v-else>
		<!-- Deprecated - backwards compatibility -->
		<div v-if="isRun" class="card-footer-container flex-space-between--center" :class="{ plain: plainMode }">
			<div v-if="isWidgetInjection" class="display-flex flex-row flex-align-center powered-by mt-1">
				powered by
				<AppIcon name="widget-ply-logo" class="ml-1" :width="30" />
			</div>
			<div v-else />
			<v-skeleton-loader v-if="widgetLoading" class="skeleton-actions" width="400" height="40" type="actions"></v-skeleton-loader>
			<div class="buttons-container" v-else>
				<FooterSecondaryButton
					v-if="!isEndRun"
					class="btn-widget-form-cancel ml-2 mt-1"
					@click="$emit('onCancel')"
					:disabled="isSubmitting || disableCancelButton"
					:plainMode="plainMode"
				>
					Cancel
				</FooterSecondaryButton>
				<FooterPrimaryButton
					:disabled="isSubmitButtonDisabled"
					class="btn-widget-form-done ml-2 mt-1"
					@click="$emit('onDone')"
					:plainMode="plainMode"
				>
					{{ doneBtnLabel }}
				</FooterPrimaryButton>
			</div>
		</div>
		<div v-else class="card-footer-container flex-space-between--center" :class="{ plain: plainMode }">
			<div v-if="!isOfTypeTool" class="display-flex flex-row flex-align-center powered-by mt-1 mr-2">
				powered by
				<AppIcon name="widget-ply-logo" class="ml-1" :width="30" />
			</div>
			<div v-else />
			<div class="striped display-flex flex-row">
				<v-btn v-if="!isEndRunWidget" class="btn-widget-form-cancel mt-1 mr-2" @click="$emit('onCancel')">Cancel</v-btn>
				<AppTooltip position="top" text="This button can only be clicked in preview mode">
					<SubmitButtonSettings disabled class="mt-1 disabled" @click="$emit('onDone')" />
				</AppTooltip>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGettersShorthand } from '@/store/utils/mappers';
import { isEndRunWidget } from '@/store/modules/selectedNode/getters';
import { isOfTypeTool } from '@/store/modules/selectedWorkflow/getters';

import WidgetFieldMixin from '../WidgetFieldMixin';

import { downloadURI } from '@/utils/helpers';
import WidgetFormService from '../../../WidgetForm/WidgetFormService';
import SubmitButtonSettings from '../../FieldSettings/WidgetSettings/SubmitButtonSettings';

import { ACTION_TYPES, OPEN_URL_OPTIONS, BUTTONS_STYLES } from '../../FieldSettings/EndRunButtonsFieldSettings/end-run-field.const';
import FooterPrimaryButton from './FooterPrimaryButton.vue';
import FooterSecondaryButton from './FooterSecondaryButton.vue';

const getItem = (text, onClick) => ({ text, onClick });

const safeParse = (stringNum) => (isNaN(parseFloat(stringNum)) ? 0 : parseFloat(stringNum));

const MAX_EXTERNAL_BUTTONS = 3;

export default {
	name: 'FooterField',
	props: {
		footerField: { type: Object },
		previousSteps: { type: Object },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
		configurationId: { type: Number },
		isRun: { type: Boolean },
		isWidgetInjection: { type: Boolean },
		isEndRun: { type: Boolean },
		isSubmitButtonDisabled: { type: Boolean },
		widgetLoading: { type: Boolean },
		isSubmitting: { type: Boolean },
		doneBtnLabel: { type: String }, // backwards compatibility
		plainMode: { type: Boolean },
		disableCancelButton: { type: Boolean },
	},
	components: {
		SubmitButtonSettings,
		FooterPrimaryButton,
		FooterSecondaryButton,
	},
	mixins: [WidgetFieldMixin],
	data() {
		return { BUTTONS_STYLES, buttonsInMenu: 0, isOpen: true, items: [], maxRetriesForMenuButtons: 20 };
	},
	computed: {
		...mapGettersShorthand({ isEndRunWidget, isOfTypeTool }),
		buttonItems() {
			return (this.fieldOptions.button_items || []).toReversed();
		},
		maxExternalNonDefaultButtons() {
			let maxNum = MAX_EXTERNAL_BUTTONS;
			if (this.fieldOptions.secondary_button_enabled) {
				maxNum -= 1;
			}
			if (this.fieldOptions.primary_button_enabled) {
				maxNum -= 1;
			}
			return maxNum;
		},
		buttonsOptions() {
			return this.buttonItems.toSpliced(this.maxExternalNonDefaultButtons);
			// return [...(this.fieldOptions.button_items || [])].reverse().splice(this.buttonsInMenu);
		},
		restOfButtonsOptions() {
			return this.buttonItems.slice(this.maxExternalNonDefaultButtons).map((item) => getItem(item.label, () => this.onButtonClick(item)));
		},
		buttonsClass() {
			const resultMap = {
				left: 'flex-start--center',
				center: 'flex-centerize',
				right: 'flex-end--center',
			};
			return resultMap[this.fieldOptions.alignment_style];
		},
		menuActivatorSize() {
			return this.plainMode ? '25px' : '36px';
		},
	},
	methods: {
		onButtonClick(btn) {
			switch (btn.action) {
				case ACTION_TYPES.OPEN_URL:
					this.$emit('openUrl', {
						url: this.processInsertedData(btn.openUrl),
						isOpenInNewTab: btn.openUrlSelection === OPEN_URL_OPTIONS.OPEN_IN_NEW_TAB,
					});
					break;
				case ACTION_TYPES.DOWNLOAD_FILE:
					downloadURI(
						this.processInsertedData(btn.downloadFileUrl),
						'downloaded_file',
						() => this.$toast.success('Downloading file...'),
						() => this.$toast.error('Something went wrong...')
					);
					break;
				case ACTION_TYPES.CLOSE_MODAL:
					this.$emit('onCloseWidget');
					break;
				case ACTION_TYPES.RUN_FEATURE_AGAIN:
					this.$emit('restartRun');
					break;
				case ACTION_TYPES.COPY_TO_CLIPBOARD:
					this.$emit('copyToClipboard', this.processInsertedData(btn.copyToClipboardText));
					break;
				default:
					break;
			}
		},
		processInsertedData(stringToProcess) {
			return WidgetFormService.processPreviousSteps(
				stringToProcess,
				this.previousSteps,
				this.replaceInsertDataStringDict,
				this.configurationsMappingBlocksUUIDs
			);
		},
		isPrimary(btn) {
			return [undefined, BUTTONS_STYLES.PRIMARY].includes(btn.buttonStyleSelection);
		},
		determineButtonsInMenu() {
			const moveButtonToMenu = () => {
				const menuItems = [...(this.fieldOptions.button_items || [])].reverse().splice(0, this.buttonsInMenu);
				const newItems = menuItems.map((item) => getItem(item.label, () => this.onButtonClick(item)));
				this.$set(this, 'items', newItems);
			};

			this.maxRetriesForMenuButtons = this.maxRetriesForMenuButtons - 1;

			if (!this.$refs?.footer_ref) {
				if (this.maxRetriesForMenuButtons > 0) {
					setTimeout(this.determineButtonsInMenu, 200);
				}
				return;
			}

			const computedStyle = getComputedStyle(this.$refs.footer_ref);
			const height = this.$refs.footer_ref?.clientHeight - safeParse(computedStyle?.paddingTop) - safeParse(computedStyle?.paddingBottom);
			if (height > 60 && this.maxRetriesForMenuButtons > 0) {
				this.buttonsInMenu += 1;

				this.$nextTick(() => {
					moveButtonToMenu();
					this.$nextTick(this.determineButtonsInMenu);
				});
			}
		},
	},
	mounted() {
		// this.$nextTick(this.determineButtonsInMenu);
	},
};
</script>

<style scoped lang="scss">
$line-color: #d6d7f9;
$fill-color: var(--bold-border-color);

.buttons-section {
	button {
		margin-left: 10px;
		margin-bottom: 3px;
	}

	::v-deep(.v-btn) {
		text-transform: none !important;
	}
	::v-deep(.v-btn__content) {
		z-index: 9;
	}
}
.card-footer-container {
	width: 100%;
	margin-top: 20px;
	padding: 14px 20px 16px 20px;
	&.plain {
		// margin: 0px;
		padding: 0px;
	}
}

.powered-by {
	font-size: 10px;
	color: var(--secondary-font-color);
}
button.striped::after,
.striped button::after {
	content: '';
	position: absolute;
	width: 100%;
	height: 100%;
	background: repeating-linear-gradient(135deg, transparent, transparent 6px, $line-color 7px, $line-color 7px) !important;
}
.menu-icon {
	margin: -5px 0 0 -2px;
}
.three-dot-button {
	background: transparent !important;
	border: 1px solid #bfc7d6 !important;
}
</style>
